import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_WATER_USE_SYSTEM_SERVICE_CONNECTIONS, SET_WATER_USE_SERVICE_CONNECTION } from '../../../../../queries/engineeringQrys';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { numbersUpToTwoDecimalPlaces } from '../../../../reuseableComponents/forms/formValidation';
import '../../dwri.css';

const DwriServiceConnections = ({ pwsId, years }) => {

const [editRow, setEditRow] = React.useState(null);
const [ercReportedValue, setErcReportedValue] = React.useState('');
const [ercReportedError, setErcReportedError] = React.useState('');
const [changeJustificationValue, setChangeJustificationValue] = React.useState('');
const [changeJustificationError, setChangeJustificationError] = React.useState('');
const [mutationError, setMutationError] = React.useState('');
const user = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user"))
  : { isPublic: true };

  const [setServiceConnections] = useMutation(SET_WATER_USE_SERVICE_CONNECTION, {
    onCompleted: () => {
      refetch();
    },
    onError: (mutationErr) => {
      setMutationError(mutationErr.message || 'Failed to update service connections.');
    }
  });

  const { data, loading, error, refetch } = useQuery(GET_WATER_USE_SYSTEM_SERVICE_CONNECTIONS, {
      variables: { pwsId, years },
      skip: !pwsId || !years || years.length === 0,
      fetchPolicy: 'cache-and-network',
  });

  if (error) return <p className="text-danger">Error loading Service Connections</p>;
  if (loading) return <p>Loading...</p>;

  const rows = data?.getWaterUseSystemServiceConnectionsPerYear || [];

  function handleEditClick(row) {
    setEditRow(row);
    setErcReportedValue(row.ercValue == null ? '' : String(row.ercValue));
    setErcReportedError('');
    setChangeJustificationValue(row.comments ?? '');
    setChangeJustificationError('');
    setMutationError('');
  }

  function handleModalClose() {
    setEditRow(null);
    setErcReportedValue('');
    setErcReportedError('');
    setChangeJustificationValue('');
    setChangeJustificationError('');
    setMutationError('');
  }

  function handleErcReportedChange(event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);

    if (validationResult === true) {
      setErcReportedValue(nextValue);
      setErcReportedError('');
      return;
    }

    setErcReportedError(validationResult);
  }

  function handleErcReportedBlur() {
    const validationResult = numbersUpToTwoDecimalPlaces(ercReportedValue);
    setErcReportedError(validationResult === true ? '' : validationResult);
  }

  function isErcChanged() {
    if (!editRow) {
      return false;
    }
    const previousValue = editRow.ercValue == null ? '' : String(editRow.ercValue);
    return ercReportedValue !== previousValue;
  }

  async function handleSave() {
    if (!editRow) return;

    const validationResult = numbersUpToTwoDecimalPlaces(ercReportedValue);
    if (validationResult !== true) {
      setErcReportedError(validationResult);
      return;
    }

    const changed = isErcChanged();
    if (changed && changeJustificationValue.trim() === '') {
      setChangeJustificationError('Change Justification is required when ERC Reported is changed.');
      return;
    }

    setChangeJustificationError('');
    setMutationError('');
    try {
      const parsedErcValue = ercReportedValue === '' ? null : Number(ercReportedValue);
      await setServiceConnections({
        variables: {
          pwsId,
          year: editRow.year,
          ercValue: parsedErcValue,
          comments: changeJustificationValue ?? ''
        }
      });
      handleModalClose();
    } catch (mutationErr) {
      // Error handled in onError.
    }
  }

  return (
    <>
      <Row>
        <Col><span><b>Service Connections</b></span></Col>
      </Row>
      {mutationError && (
        <Row>
          <Col>
            <div className="text-danger mb-2">{mutationError}</div>
          </Col>
        </Row>
      )}
      <div className="table-responsive">
        <Row className="dwri-service-connections-header gx-0">
          <Col className="dwri-table-header-cell dwri-col-year"><span><b>Year</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Residential</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Commercial</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Industrial</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Institutional</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Total</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>ERC Value</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>ERC Accepted</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Comments</b></span></Col>
          <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
        </Row>
        {rows.map((row, idx) => (
          <Row key={row.year} className={`dwri-service-connections-row gx-0 ${idx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
            <Col className="dwri-table-cell dwri-col-year"><span>{row.year}</span></Col>
            <Col className="dwri-table-cell"><span>{row.residential}</span></Col>
            <Col className="dwri-table-cell"><span>{row.commercial}</span></Col>
            <Col className="dwri-table-cell"><span>{row.industrial}</span></Col>
            <Col className="dwri-table-cell"><span>{row.institutional}</span></Col>
            <Col className="dwri-table-cell"><span>{row.total}</span></Col>
            <Col className="dwri-table-cell"><span>{row.ercValue}</span></Col>
            <Col className="dwri-table-cell"><span>{row.ercAccepted ? 'Yes' : 'No'}</span></Col>
            <Col className="dwri-table-cell"><span>{row.comments}</span></Col>
            {user?.permissions?.includes('edit_water_use_system') && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit service connections for year ${row.year}`}
                type="button"
                onClick={() => handleEditClick(row)}
              >
                Edit
              </button>
            </Col>
            )}
          </Row>
        ))}
      </div>
      {editRow && (
        <Modal
          onClose={handleModalClose}
          onEscape={handleModalClose}
        >
          <ModalTitle>Edit Service Connections</ModalTitle>
          <ModalContent>
            <div className="dwri-service-modal-grid">
              <div className="dwri-service-modal-column">
                <TextInput label="Year" value={String(editRow.year ?? '')} isDisabled />
                <TextInput label="Commercial" value={String(editRow.commercial ?? '')} isDisabled />
                <TextInput label="Institutional" value={String(editRow.institutional ?? '')} isDisabled />
              </div>
              <div className="dwri-service-modal-column">
                <TextInput label="Residential" value={String(editRow.residential ?? '')} isDisabled />
                <TextInput label="Industrial" value={String(editRow.industrial ?? '')} isDisabled />
                <TextInput
                  label="ERC Reported"
                  value={ercReportedValue}
                  onChange={handleErcReportedChange}
                  onBlur={handleErcReportedBlur}
                  errorMessage={ercReportedError || undefined}
                />
              </div>
              <div className="dwri-service-modal-full-width">
                <TextArea
                  label="Change Justification"
                  value={changeJustificationValue}
                  onChange={(event) => {
                    setChangeJustificationValue(event.target.value);
                    setChangeJustificationError('');
                  }}
                  isRequired={isErcChanged()}
                />
                {changeJustificationError && (
                  <div className="text-danger mt-1">{changeJustificationError}</div>
                )}
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="dwri-service-modal-footer">
            <button type="button" onClick={handleModalClose}>Close</button>
            <button type="button" onClick={handleSave}>Save</button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );

}

export default DwriServiceConnections;